/**
 * DevApiBanner.tsx
 *
 * File-level:
 * - Small development-only visual helper that shows the resolved API base
 *   (apiBase()) so developers can quickly identify where API requests will go.
 *
 * Notes:
 * - Only visible on localhost / 127.0.0.1 (development).
 * - Includes a copy-to-clipboard button for convenience and a "Use this base"
 *   button that applies the base to window.__API_BASE__ for instant runtime override.
 */

import React, { useMemo, useState } from 'react';
import { apiBase } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Copy, Check, Link2 } from 'lucide-react';

/**
 * DevApiBanner
 *
 * @component DevApiBanner
 * @description
 * - Renders a small fixed badge showing the resolved API base.
 * - Visible only during local development (hostname localhost/127.0.0.1/0.0.0.0).
 * - Adds "Use this base" button to set window.__API_BASE__ to the resolved base.
 */
export default function DevApiBanner(): JSX.Element | null {
  const [copied, setCopied] = useState(false);
  const [applied, setApplied] = useState(false);

  const resolved = useMemo(() => {
    try {
      return apiBase();
    } catch {
      return '(unable to resolve)';
    }
  }, []);

  const isLocalhost =
    typeof window !== 'undefined' && ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);

  if (!isLocalhost) return null;

  /**
   * copyToClipboard
   *
   * @description Copy the resolved API base to clipboard and show a temporary confirmation.
   */
  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(resolved);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }

  /**
   * applyRuntimeBase
   *
   * @description Sets window.__API_BASE__ to the resolved base so the app will use it
   * for subsequent API requests. Shows a temporary confirmation label.
   */
  function applyRuntimeBase() {
    try {
      if (typeof window === 'undefined') return;
      // Apply the runtime override
      (window as any).__API_BASE__ = resolved;
      setApplied(true);
      // Temporary feedback
      window.setTimeout(() => setApplied(false), 2000);
      // Also copy to clipboard as convenience
      navigator.clipboard?.writeText(resolved).catch(() => {});
      // Note: No full reload — modules that call apiBase() at request time will pick this up.
    } catch {
      setApplied(true);
      window.setTimeout(() => setApplied(false), 2000);
    }
  }

  return (
    <div className="fixed left-4 bottom-4 z-50">
      <div className="flex items-center space-x-3 bg-white border rounded-lg px-3 py-2 shadow-md text-xs">
        <div className="min-w-[220px]">
          <div className="text-[11px] text-gray-500">API Base</div>
          <div className="text-sm font-medium text-slate-800 truncate" title={resolved}>
            {resolved}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" className="bg-transparent border-gray-200 text-gray-700" onClick={copyToClipboard}>
            {copied ? <Check className="w-4 h-4 mr-2 text-green-600" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>

          <Button size="sm" className="bg-transparent border-gray-200 text-gray-700" onClick={applyRuntimeBase}>
            {applied ? <Check className="w-4 h-4 mr-2 text-green-600" /> : <Link2 className="w-4 h-4 mr-2" />}
            {applied ? 'Applied' : 'Use this base'}
          </Button>
        </div>
      </div>
    </div>
  );
}
